import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/components/ui/use-toast";

export interface Session {
  id: string;
  mentor_name: string;
  mentor_id: string;
  mentor_image_url?: string | null;
  game_name: string;
  scheduled_date: string;
  scheduled_start: string;
  scheduled_end: string;
  status: string;
  payment_released: boolean;
  mentorship_title?: string;
  mentorship_cover_url?: string | null;
  meeting_link?: string;
  has_review?: boolean;
  created_at: string; // Data de compra da sessão
}

// Cache simples para evitar re-fetches desnecessários
const sessionsCache: { [userId: string]: { data: Session[], timestamp: number } } = {};
const CACHE_DURATION = 10000; // 10 segundos (reduzido para atualizações mais rápidas)

export const usePlayerSessions = () => {
  const [sessions, setSessions] = useState<Session[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { toast } = useToast();

  // Memoizar o userId para evitar re-renders desnecessários
  const userId = useMemo(() => user?.id, [user?.id]);

  // Função para enriquecer sessões com dados de mentoria em background
  const enrichSessionsWithMentorshipData = useCallback(async (currentSessions: Session[], userId: string) => {
    console.log(`🚀 [enrichSessionsWithMentorshipData] INICIANDO - Total de sessões: ${currentSessions.length}`);
    
    // Filtrar sessões que não têm dados de mentoria ou têm título genérico
    const sessionsWithoutOffering = currentSessions.filter(session => 
      (!session.mentorship_cover_url || session.mentorship_title?.includes('Mentoria de')) && 
      session.mentor_id
    );

    if (sessionsWithoutOffering.length === 0) {
      console.log(`✅ [enrichSessionsWithMentorshipData] Todas as sessões já têm dados de mentoria`);
      return;
    }

    console.log(`🔍 [enrichSessionsWithMentorshipData] Tentando enriquecer ${sessionsWithoutOffering.length} sessões:`);
    sessionsWithoutOffering.forEach(session => {
      console.log(`  - Sessão ${session.id}: Mentor ${session.mentor_name} (${session.mentor_id}), Jogo: ${session.game_name}, Título atual: "${session.mentorship_title}"`);
    });

    try {
      // Para cada sessão sem dados de mentoria, buscar uma oferta do mentor para o mesmo jogo
      const enrichmentPromises = sessionsWithoutOffering.map(async (session) => {
        try {
          console.log(`🔍 [enrichSessionsWithMentorshipData] Processando sessão ${session.id} - Mentor: ${session.mentor_name}`);
          
          // Primeiro, buscar o game_id baseado no nome do jogo
          const { data: gameData, error: gameError } = await supabase
            .from('games')
            .select('id')
            .eq('name', session.game_name)
            .maybeSingle();

          if (gameError || !gameData) {
            console.warn(`❌ [enrichSessionsWithMentorshipData] Jogo "${session.game_name}" não encontrado:`, gameError);
            return null;
          }

          console.log(`🎮 [enrichSessionsWithMentorshipData] Game ID encontrado: ${gameData.id} para jogo "${session.game_name}"`);

          // Primeiro, tentar buscar ofertas ativas
          let { data: offeringData, error } = await supabase
            .from('mentorship_offerings')
            .select('title, cover_image_url, is_active')
            .eq('mentor_id', session.mentor_id)
            .eq('game_id', gameData.id)
            .eq('is_active', true)
            .limit(1)
            .maybeSingle();

          // Se não encontrar ofertas ativas, buscar qualquer oferta (incluindo inativas)
          if ((!offeringData || error) && !error?.message?.includes('multiple')) {
            console.log(`⚠️ [enrichSessionsWithMentorshipData] Nenhuma oferta ativa encontrada, buscando ofertas inativas para ${session.mentor_name}`);
            
            const { data: inactiveOffering, error: inactiveError } = await supabase
              .from('mentorship_offerings')
              .select('title, cover_image_url, is_active')
              .eq('mentor_id', session.mentor_id)
              .eq('game_id', gameData.id)
              .order('created_at', { ascending: false })
              .limit(1)
              .maybeSingle();
              
            if (inactiveOffering && !inactiveError) {
              offeringData = inactiveOffering;
              console.log(`✅ [enrichSessionsWithMentorshipData] Usando oferta inativa para ${session.mentor_name}: ${inactiveOffering.title}`);
            }
          }

          if (error) {
            console.warn(`❌ [enrichSessionsWithMentorshipData] Erro ao buscar offering para mentor ${session.mentor_id}:`, error);
            return null;
          }

          if (offeringData) {
            console.log(`✅ [enrichSessionsWithMentorshipData] Dados encontrados para sessão ${session.id}:`, {
              title: offeringData.title,
              hasCover: !!offeringData.cover_image_url
            });
            return {
              sessionId: session.id,
              title: offeringData.title,
              coverUrl: offeringData.cover_image_url
            };
          } else {
            console.warn(`❓ [enrichSessionsWithMentorshipData] Nenhuma oferta ativa encontrada para mentor ${session.mentor_name} (${session.mentor_id}) no jogo ${session.game_name}`);
          }
        } catch (error) {
          console.error(`❌ [enrichSessionsWithMentorshipData] Erro para sessão ${session.id}:`, error);
        }
        return null;
      });

      const enrichmentResults = await Promise.allSettled(enrichmentPromises);
      const validEnrichments = enrichmentResults
        .filter(result => result.status === 'fulfilled' && result.value)
        .map(result => (result as PromiseFulfilledResult<any>).value);

      if (validEnrichments.length > 0) {
        console.log(`✅ [enrichSessionsWithMentorshipData] Enriquecendo ${validEnrichments.length} sessões`);
        
        // Atualizar as sessões com os novos dados
        setSessions(prevSessions => 
          prevSessions.map(session => {
            const enrichment = validEnrichments.find(e => e.sessionId === session.id);
            if (enrichment) {
              return {
                ...session,
                mentorship_title: enrichment.title || session.mentorship_title,
                mentorship_cover_url: enrichment.coverUrl || session.mentorship_cover_url
              };
            }
            return session;
          })
        );

        // Atualizar cache também
        if (sessionsCache[userId]) {
          sessionsCache[userId].data = sessionsCache[userId].data.map(session => {
            const enrichment = validEnrichments.find(e => e.sessionId === session.id);
            if (enrichment) {
              return {
                ...session,
                mentorship_title: enrichment.title || session.mentorship_title,
                mentorship_cover_url: enrichment.coverUrl || session.mentorship_cover_url
              };
            }
            return session;
          });
        }
      }
    } catch (error) {
      console.error(`❌ [enrichSessionsWithMentorshipData] Erro geral:`, error);
    }
  }, []);

  const fetchSessions = useCallback(async (forceRefresh = false) => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Verificar cache primeiro (apenas se não for refresh forçado)
    if (!forceRefresh && sessionsCache[userId]) {
      const cached = sessionsCache[userId];
      const isExpired = Date.now() - cached.timestamp > CACHE_DURATION;
      
      if (!isExpired) {
        console.log("📋 [usePlayerSessions] Usando dados do cache");
        setSessions(cached.data);
        setLoading(false);
        return;
      }
    }

    try {
      console.log("🔄 [usePlayerSessions] Buscando sessões para usuário:", userId);
      
      // Query otimizada com LEFT JOINs para buscar tudo de uma vez
      const { data: sessionData, error: sessionError } = await supabase
        .from("sessions")
        .select(`
          id,
          scheduled_date,
          scheduled_start,
          scheduled_end,
          status,
          payment_released,
          game_id,
          mentor_id,
          meeting_link,
          offering_id,
          created_at,
          games!inner(name),
          mentor_info!inner(
            profile_id, 
            image_url, 
            profiles!inner(nickname)
          ),
          mentorship_offerings!offering_id(title, cover_image_url),
          payments(id, amount_cents, status, stripe_payment_id),
          session_reviews!left(id)
        `)
        .eq("player_profile_id", userId)
        .order("created_at", { ascending: false });

      if (sessionError) {
        console.error("❌ [usePlayerSessions] Erro ao buscar sessões:", sessionError);
        throw sessionError;
      }

      if (!sessionData || sessionData.length === 0) {
        console.log("📭 [usePlayerSessions] Nenhuma sessão encontrada para usuário:", userId);
        setSessions([]);
        setLoading(false);
        
        // Cache resultado vazio
        sessionsCache[userId] = { data: [], timestamp: Date.now() };
        return;
      }

      console.log(`✅ [usePlayerSessions] ${sessionData.length} sessões encontradas`);

      // Processar dados em uma única passada - MUITO MAIS RÁPIDO
      const formattedSessions: Session[] = sessionData.map(session => {
        // Extrair dados do mentor de forma segura
        const mentorName = session.mentor_info?.profiles?.nickname || "Mentor";
        const mentorImageUrl = session.mentor_info?.image_url || null;
        const mentorProfileId = session.mentor_info?.profile_id || session.mentor_id;
        
        // Extrair nome do jogo
        const gameName = session.games?.name || "Jogo não especificado";
        
        // Determinar status (otimizado - sem verificação adicional)
        let status = session.status;
        if (session.payments?.length > 0) {
          const payment = session.payments[0];
          if (payment.status === "completed" && status === "pending") {
            status = "confirmado";
          }
        }
        
        // Verificar se tem review (usando left join - SEM query adicional)
        const hasReview = session.session_reviews && session.session_reviews.length > 0;
        
        // Título da mentoria com fallback mais inteligente
        let mentorshipTitle = session.mentorship_offerings?.title || 
                             `Mentoria de ${gameName} com ${mentorName}`;
        let mentorshipCoverUrl = session.mentorship_offerings?.cover_image_url || null;
        
        // Se não temos dados da mentoria (offering_id null), tentar buscar uma oferta do mentor para o mesmo jogo
        if (!session.mentorship_offerings && session.mentor_id && session.game_id) {
          // Marcar para busca posterior - isso será feito em uma query separada para não afetar performance
          console.log(`⚠️ [usePlayerSessions] Sessão ${session.id} sem offering_id - dados limitados disponíveis`);
        }
        
        const meetingLink = session.meeting_link || "";
        
        // Log para debug - verificar se meeting_link está sendo carregado
        if (session.id && !meetingLink) {
          console.log(`⚠️ [usePlayerSessions] Sessão ${session.id} sem meeting_link - Status: ${status}, Mentor: ${mentorName}`);
        } else if (session.id && meetingLink) {
          console.log(`✅ [usePlayerSessions] Sessão ${session.id} com meeting_link encontrado`);
        }
        
        return {
          id: session.id,
          mentor_name: mentorName,
          mentor_id: mentorProfileId,
          mentor_image_url: mentorImageUrl,
          game_name: gameName,
          scheduled_date: session.scheduled_date,
          scheduled_start: session.scheduled_start,
          scheduled_end: session.scheduled_end,
          status: status,
          payment_released: session.payment_released || false,
          mentorship_title: mentorshipTitle,
          mentorship_cover_url: mentorshipCoverUrl,
          meeting_link: meetingLink,
          has_review: hasReview || false,
          created_at: session.created_at
        };
      });

      console.log(`🎯 [usePlayerSessions] Sessões formatadas: ${formattedSessions.length}`);
      
      // Atualizar cache
      sessionsCache[userId] = { data: formattedSessions, timestamp: Date.now() };
      
      setSessions(formattedSessions);

      // Verificação de pagamentos em background (não bloqueia UI)
      verifyPendingPaymentsInBackground(sessionData, userId);
      
      // Buscar dados de mentoria para sessões sem offering_id (em background)
      enrichSessionsWithMentorshipData(formattedSessions, userId);

    } catch (error: any) {
      console.error("❌ [usePlayerSessions] Erro ao buscar sessões:", error);
      toast({
        variant: "destructive",
        title: "Erro ao carregar sessões",
        description: "Não foi possível carregar suas sessões. Tente novamente mais tarde."
      });
    } finally {
      setLoading(false);
    }
  }, [userId, toast, enrichSessionsWithMentorshipData]);

  // Função para verificar pagamentos em background
  const verifyPendingPaymentsInBackground = useCallback(async (sessionData: any[], userId: string) => {
    const pendingSessions = sessionData.filter(session => 
      session.status === 'pending' && 
      session.payments?.length > 0 && 
      session.payments[0].status === 'pending' && 
      session.payments[0].stripe_payment_id
    );

    if (pendingSessions.length === 0) return;

    console.log(`🔍 [usePlayerSessions] Verificando ${pendingSessions.length} pagamentos pendentes em background`);

    // Verificar pagamentos em paralelo (máximo 3 por vez para não sobrecarregar)
    const batchSize = 3;
    for (let i = 0; i < pendingSessions.length; i += batchSize) {
      const batch = pendingSessions.slice(i, i + batchSize);
      
      const verificationPromises = batch.map(async (session) => {
        try {
          const { error } = await supabase.functions.invoke("verify-payment", {
            body: { 
              sessionId: session.id,
              stripeSessionId: session.payments[0].stripe_payment_id
            }
          });
          
          if (error) {
            console.error(`❌ [Background] Erro ao verificar pagamento ${session.id}:`, error);
          } else {
            console.log(`✅ [Background] Pagamento verificado ${session.id}`);
          }
        } catch (error) {
          console.error(`💥 [Background] Erro na verificação ${session.id}:`, error);
        }
      });

      await Promise.allSettled(verificationPromises);
      
      // Pequena pausa entre batches
      if (i + batchSize < pendingSessions.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    // Invalidar cache apenas - NÃO recarregar automaticamente para evitar loops
    if (pendingSessions.length > 0) {
      console.log("🔄 [Background] Cache invalidado após verificação de pagamentos");
      delete sessionsCache[userId];
    }
  }, []);

  // Função para invalidar cache manualmente
  const invalidateCache = useCallback(() => {
    if (userId && sessionsCache[userId]) {
      delete sessionsCache[userId];
    }
  }, [userId]);

  // Função de refetch otimizada
  const refetch = useCallback((forceRefresh = false) => {
    if (forceRefresh) {
      invalidateCache();
    }
    setLoading(true);
    return fetchSessions(forceRefresh);
  }, [fetchSessions, invalidateCache]);

  useEffect(() => {
    fetchSessions();
    
    // Subscription em tempo real para atualizar quando meeting_link for alterado
    if (!userId) return;
    
    console.log('👂 [usePlayerSessions] Configurando subscription real-time para sessões do jogador');
    
    const channel = supabase
      .channel(`player-sessions-${userId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'sessions',
          filter: `player_profile_id=eq.${userId}`
        },
        (payload) => {
          console.log('🔄 [usePlayerSessions] Sessão atualizada via real-time:', payload);
          
          // Verificar se o meeting_link foi atualizado
          if (payload.new.meeting_link && payload.old.meeting_link !== payload.new.meeting_link) {
            console.log('🔗 [usePlayerSessions] meeting_link atualizado! Invalidando cache e recarregando...');
            
            // Invalidar cache
            invalidateCache();
            
            // Recarregar sessões
            fetchSessions(true);
          } else {
            // Para outras atualizações, apenas invalidar cache (não recarregar imediatamente)
            console.log('🔄 [usePlayerSessions] Outra atualização detectada, invalidando cache');
            invalidateCache();
          }
        }
      )
      .subscribe();
    
    return () => {
      console.log('🔇 [usePlayerSessions] Removendo subscription real-time');
      supabase.removeChannel(channel);
    };
  }, [fetchSessions, userId, invalidateCache]);

  return { 
    sessions, 
    loading, 
    refetch,
    invalidateCache
  };
};
